import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useOAuthCallback } from '@/hooks/useAuth'
import { refreshAdminTier } from '@/lib/adminTier'
import { fetchLaunchableAgents } from '@/hooks/useAgentCatalogue'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/stores/auth'
import { getSafeReturnTo } from '@/lib/safeReturnTo'
import { MAIN_APP_ROUTES } from '@/lib/routes'

/**
 * Handles OAuth redirect callback.
 *
 * URL: /auth/callback/:provider?code=...&state=...
 *
 * Flow:
 * 1. Extract code and state from URL params
 * 2. POST to backend /auth/oauth/{provider}/callback
 * 3. Store JWT + user in Zustand
 * 4. Redirect to /onboarding (an open onboarding run) or /home
 *
 * A SIGNED-IN nurse can legitimately be here: the MCP consent screen at
 * /oauth/authorize sends her through /login?returnTo=… to re-authenticate. So
 * "already has a session" does NOT mean "this URL is spent" — the thing that
 * distinguishes them is `oauth_state`, which useOAuthLogin writes immediately
 * before handing off to the provider and the first instance to run consumes.
 * Present means a flow is in progress; absent on a signed-in nurse means she
 * came back to a URL that has already been used (browser Back, a second tab).
 */
export default function OAuthCallback() {
  const { provider } = useParams<{ provider: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { mutateAsync, isError } = useOAuthCallback()

  const code = searchParams.get('code')
  const state = searchParams.get('state')

  // React StrictMode double-invokes mount effects in development. Everything
  // else this effect depends on is referentially stable — `mutateAsync` is
  // memoised on the mutation observer, unlike the full result object, whose
  // identity churns idle → pending → success and would re-run the effect (and
  // re-POST a single-use code) on every one of those renders.
  const hasTriggered = useRef(false)

  // WHERE THIS SIGN-IN LANDS, once something has answered.
  //
  // `landing` says the exchange is done and this page is now waiting for a
  // destination; the effect below supplies it when landingFor stops
  // saying 'wait'. A subscription and not a one-shot read, because the answer
  // The activation run that used to decide the landing (the onboarding
  // interview) died with the session cut-over; everyone lands on home.
  const [landing, setLanding] = useState(false)
  useEffect(() => {
    if (!landing) return
    void navigate(MAIN_APP_ROUTES.home, { replace: true })
  }, [landing, navigate])

  useEffect(() => {
    if (hasTriggered.current) return
    hasTriggered.current = true

    if (!provider || !code || !state) {
      void navigate('/login', { replace: true })
      return
    }

    // Client-side CSRF check: verify the state matches what we stored before
    // redirecting to the OAuth provider. This replaces the cookie-based check
    // that Firebase Hosting was stripping (only __session cookies pass through).
    const savedState = sessionStorage.getItem('oauth_state')
    sessionStorage.removeItem('oauth_state')

    // No state stored, but a session exists: a spent callback URL revisited, not
    // a forged request. Send her into the app rather than showing an alarming
    // "Sitzung abgelaufen" for a login that already worked. Read imperatively
    // because this is the value at the moment the flow starts — subscribing
    // would re-render on the identity this very page is about to install.
    const signedInUser = useAuthStore.getState().user
    if (savedState === null && signedInUser !== null) {
      setLanding(true)
      return
    }

    if (savedState !== state) {
      void navigate('/login?error=session_expired', { replace: true })
      return
    }

    // Capture the MCP-consent returnTo SYNCHRONOUSLY here, alongside oauth_state
    // and BEFORE the async token exchange, so the login-time teardown in setAuth
    // cannot empty sessionStorage first and leave an SSO login resuming nothing.
    // Password login never hit this — it reads returnTo from the URL, not
    // sessionStorage.
    const storedReturnTo = sessionStorage.getItem('oauth_return_to')
    sessionStorage.removeItem('oauth_return_to')

    // Awaited inline rather than driven from mutate()'s onSuccess/onError.
    // Signing in as a DIFFERENT account is a real identity swap, so the store
    // bumps identityEpoch and <AuthIdentityBoundary> legitimately remounts this
    // subtree mid-exchange — and mutationObserver gates the mutate() callbacks
    // on `hasListeners()`, so they are dropped when that unmount happens, taking
    // the navigation and the returnTo with them. A promise continuation survives
    // it, and `navigate` is safe to call from one because useNavigate() binds to
    // the Router, which sits ABOVE the boundary in App.tsx and is never
    // remounted. Same shape as VerifyEmail's runVerify.
    void (async () => {
      try {
        // The response is not read for a destination: the store holds the
        // identity this exchange installed, and reading the landing off one
        // source keeps this page from disagreeing with the guard behind it.
        await mutateAsync({ provider, code, state })
      } catch {
        // The hook's onError owns the toast; this page owns the route.
        void navigate('/login', { replace: true })
        return
      }

      // The admin tier (/admin) and the launchable list (/agents) are lazy
      // post-boot reads (care-app/ADR-0015), fired here in the continuation:
      // this page is the one boot path that skips useAuthInit's refresh, so
      // without the pair a Google login lands with the admin nav hidden until
      // a manual reload. mutateAsync resolves after the mutation-level
      // onSuccess has run setAuth (in the installed @tanstack/query-core 5.x
      // hook-level onSuccess is unconditional — the observer-gated,
      // droppable callbacks are only the per-mutate() second argument), so
      // the pair sees the committed session. Fire-and-forget like every
      // other session install.
      void refreshAdminTier()
      void fetchLaunchableAgents()

      // Resume the flow that sent the user to /login (MCP consent at
      // /oauth/authorize, sub-app hops). Re-validated so a corrupt value
      // degrades to the default landing, mirroring Login.handleSubmit.
      const returnTo = storedReturnTo
        ? getSafeReturnTo(new URLSearchParams({ returnTo: storedReturnTo }))
        : null
      if (returnTo) {
        // A COMMITTED browser navigation, not an SPA navigate, even when
        // returnTo is same-origin: the app reboots authenticated (persisted
        // token + refresh cookie) straight onto /oauth/authorize, which no
        // later render can override. getSafeReturnTo has already constrained
        // returnTo to a same-site host.
        window.location.assign(returnTo)
      } else {
        setLanding(true)
      }
    })()
  }, [provider, code, state, navigate, mutateAsync])

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
      <div className="text-center space-y-4">
        <Spinner className="h-8 w-8 mx-auto" />
        {isError ? (
          <p className="text-sm text-error">Login failed. Redirecting...</p>
        ) : (
          <p className="text-sm text-neutral-500">Signing you in...</p>
        )}
      </div>
    </div>
  )
}
